import React, { useState } from 'react';
import { Box, Typography, Container, TextField, Button, Alert, Checkbox, FormControlLabel, Link } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useDispatch } from 'react-redux';
import { sendContactForm } from '../../../store/contactSlice';
import { AppDispatch } from '../../../store/store';

interface SpamFilter {
    containsSpamKeywords: (text: string) => boolean;
    isValidEmail: (email: string) => boolean;
    hasRepeatedChars: (text: string) => boolean;
    containsUrls: (text: string) => boolean;
}

const spamFilter: SpamFilter = {
    containsSpamKeywords: (text: string): boolean => {
        const spamKeywords = [
            'viagra', 'casino', 'lottery', 'winner', 'congratulations',
            'click here', 'free money', 'earn money fast', 'get rich',
            'weight loss', 'diet pills', 'refinance', 'mortgage',
            'investment opportunity', 'make money online', 'work from home',
            'bitcoin', 'cryptocurrency investment', 'trading bot'
        ];

        const lowerText = text.toLowerCase();
        return spamKeywords.some(keyword => lowerText.includes(keyword));
    },

    isValidEmail: (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },

    hasRepeatedChars: (text: string): boolean => {
        const repeatedPattern = /(.)\1{4,}/;
        return repeatedPattern.test(text);
    },

    containsUrls: (text: string): boolean => {
        const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+|\b[a-zA-Z0-9-]+\.[a-zA-Z]{2,}\b)/;
        return urlPattern.test(text);
    }
};

export default function Contact() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | 'spam' | null>(null);
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
    const [honeypot, setHoneypot] = useState('');
    const dispatch = useDispatch<AppDispatch>();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = (): boolean => {
        if (honeypot) {
            setSubmitStatus('spam');
            return false;
        }

        if (!acceptedPrivacy) {
            setSubmitStatus('error');
            return false;
        }

        if (!spamFilter.isValidEmail(formData.email)) {
            setSubmitStatus('error');
            return false;
        }

        const combinedText = `${formData.name} ${formData.message}`;

        if (spamFilter.containsSpamKeywords(combinedText) ||
            spamFilter.hasRepeatedChars(combinedText) ||
            spamFilter.containsUrls(formData.message)) {
            setSubmitStatus('spam');
            return false;
        }

        if (formData.message.length < 10) {
            setSubmitStatus('error');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        if (!validateForm()) {
            setIsSubmitting(false);
            return;
        }

        try {
            await dispatch(sendContactForm(formData)).unwrap();
            setSubmitStatus('success');
            setFormData({ name: '', email: '', message: '' });
            setAcceptedPrivacy(false);
        } catch (error) {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            id="contact"
            sx={{
                py: 6,
                bgcolor: 'white'
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={0} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{
                            width: '100%',
                            height: { xs: '300px', md: '500px' },
                            overflow: 'hidden'
                        }}>
                            <Box
                                component="img"
                                src="/images/home/contact-us.jpg"
                                alt="Contact"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{
                            pl: { xs: 0, md: 6 },
                            mt: { xs: 4, md: 0 },
                            pr: { xs: 0, md: 2 }
                        }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    mb: 2,
                                    color: '#333',
                                    fontWeight: 'bold',
                                    fontSize: { xs: '32px', md: '48px' },
                                    lineHeight: 1.2
                                }}
                            >
                                Ready to get started?
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    mb: 4,
                                    color: '#666',
                                    fontSize: { xs: '16px', md: '20px' },
                                    lineHeight: 1.5
                                }}
                            >
                                Let's keep in touch! Write us about any questions you have!
                            </Typography>

                            {submitStatus === 'success' && (
                                <Alert
                                    severity="success"
                                    sx={{ mb: 3 }}
                                    onClose={() => setSubmitStatus(null)}
                                >
                                    Thank you for your message! We'll get back to you soon.
                                </Alert>
                            )}

                            {submitStatus === 'error' && (
                                <Alert
                                    severity="error"
                                    sx={{ mb: 3 }}
                                    onClose={() => setSubmitStatus(null)}
                                >
                                    Please check your information and try again. Make sure you accept our privacy policy.
                                </Alert>
                            )}

                            {submitStatus === 'spam' && (
                                <Alert
                                    severity="warning"
                                    sx={{ mb: 3 }}
                                    onClose={() => setSubmitStatus(null)}
                                >
                                    Your message was flagged by our spam filter. Please review your content and try again.
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <input
                                    type="text"
                                    name="website"
                                    value={honeypot}
                                    onChange={(e) => setHoneypot(e.target.value)}
                                    style={{
                                        position: 'absolute',
                                        left: '-9999px',
                                        width: '1px',
                                        height: '1px',
                                        opacity: 0
                                    }}
                                    tabIndex={-1}
                                    autoComplete="off"
                                />

                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                mb: 1,
                                                color: '#333',
                                                fontWeight: 600,
                                                fontSize: { xs: '16px', md: '18px' }
                                            }}
                                        >
                                            Your Name
                                        </Typography>
                                        <TextField
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="Please, write your name"
                                            variant="outlined"
                                            fullWidth
                                            required
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: 'transparent',
                                                    borderRadius: 0,
                                                    '& fieldset': {
                                                        borderColor: '#e0e0e0',
                                                        borderWidth: '1px',
                                                        borderRadius: 0
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#4285f4'
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#4285f4'
                                                    }
                                                },
                                                '& .MuiInputBase-input': {
                                                    py: 1.5,
                                                    fontSize: '16px',
                                                    '&::placeholder': {
                                                        color: '#adb5bd',
                                                        opacity: 1
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                mb: 1,
                                                color: '#333',
                                                fontWeight: 600,
                                                fontSize: { xs: '16px', md: '18px' }
                                            }}
                                        >
                                            Your E-mail
                                        </Typography>
                                        <TextField
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="Please, write your E-mail"
                                            variant="outlined"
                                            fullWidth
                                            required
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: 'transparent',
                                                    borderRadius: 0,
                                                    '& fieldset': {
                                                        borderColor: '#e0e0e0',
                                                        borderWidth: '1px',
                                                        borderRadius: 0
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#4285f4'
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#4285f4'
                                                    }
                                                },
                                                '& .MuiInputBase-input': {
                                                    py: 1.5,
                                                    fontSize: '16px',
                                                    '&::placeholder': {
                                                        color: '#adb5bd',
                                                        opacity: 1
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                mb: 1,
                                                color: '#333',
                                                fontWeight: 600,
                                                fontSize: { xs: '16px', md: '18px' }
                                            }}
                                        >
                                            Your Message
                                        </Typography>
                                        <TextField
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            placeholder="We are eager to hear your questions!"
                                            variant="outlined"
                                            fullWidth
                                            multiline
                                            rows={6}
                                            required
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: 'transparent',
                                                    borderRadius: 0,
                                                    '& fieldset': {
                                                        borderColor: '#e0e0e0',
                                                        borderWidth: '1px',
                                                        borderRadius: 0
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#4285f4'
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#4285f4'
                                                    }
                                                },
                                                '& .MuiInputBase-input': {
                                                    py: 1.5,
                                                    fontSize: '16px',
                                                    '&::placeholder': {
                                                        color: '#adb5bd',
                                                        opacity: 1
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={acceptedPrivacy}
                                                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                                                    sx={{
                                                        color: '#4285f4',
                                                        '&.Mui-checked': {
                                                            color: '#4285f4'
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#666',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    I agree to the{' '}
                                                    <Link
                                                        href="/privacy-policy"
                                                        target="_blank"
                                                        sx={{
                                                            color: '#4285f4',
                                                            textDecoration: 'underline'
                                                        }}
                                                    >
                                                        Privacy Policy
                                                    </Link>
                                                    {' '}and{' '}
                                                    <Link
                                                        href="/terms-of-service"
                                                        target="_blank"
                                                        sx={{
                                                            color: '#4285f4',
                                                            textDecoration: 'underline'
                                                        }}
                                                    >
                                                        Terms of Service
                                                    </Link>
                                                </Typography>
                                            }
                                            sx={{ mb: 2 }}
                                        />
                                    </Grid>

                                    <Grid size={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={isSubmitting || !acceptedPrivacy}
                                                sx={{
                                                    bgcolor: '#5C78FF',
                                                    borderRadius: '30px',
                                                    px: 4,
                                                    py: 1.5,
                                                    fontSize: '16px',
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    boxShadow: '0 4px 15px rgba(92, 120, 255, 0.3)',
                                                    '&:hover': {
                                                        bgcolor: '#4660c8',
                                                        boxShadow: '0 6px 20px rgba(92, 120, 255, 0.4)'
                                                    },
                                                    '&:disabled': {
                                                        bgcolor: '#ccc'
                                                    }
                                                }}
                                            >
                                                {isSubmitting ? 'Sending...' : 'Send Message'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </form>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}